import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
    Box,
    Container,
    Typography,
    useTheme,
    Grid,
    TextField,
    Button,
    Paper,
    IconButton,
    Stack
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import { useSnackbar } from 'notistack';

const Contact = () => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        message: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const whatsappNumber = "923007370282";
        const text = `*Hello WorldWise Scholar!*%0A%0AI would like to book a consultant.%0A%0AName: ${formData.name}%0ASubject: ${formData.subject}%0AMessage: ${formData.message}`;

        window.open(`https://wa.me/${whatsappNumber}?text=${text}`, '_blank');

        enqueueSnackbar('Opening WhatsApp...', { variant: 'info' });
        setFormData({ name: '', subject: '', message: '' });
    };

    return (
        <Box sx={{
            minHeight: '80vh',
            bgcolor: 'background.default',
            py: { xs: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden'
        }}>
            <Helmet>
                <title>Contact Us | WorldWise Scholar</title>
                <link rel="canonical" href="https://worldwisescholar.online/contact" />
            </Helmet>
            {/* Background patterns */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: theme.palette.mode === 'light' ? 0.02 : 0.05,
                zIndex: 0,
                backgroundImage: `linear-gradient(${theme.palette.divider} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.divider} 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
            }} />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={6}>
                    {/* Info Side */}
                    <Grid item xs={12} md={5}>
                        <Typography variant="h2" component="h1" gutterBottom fontWeight="900" sx={{
                            background: theme.palette.mode === 'light'
                                ? 'linear-gradient(to right, #111827, #4F46E5)'
                                : 'linear-gradient(to right, #F8FAFC, #A5B4FC)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 3
                        }}>
                            Get in Touch
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 6, fontWeight: 400 }}>
                            Have questions about scholarships or admissions? We're here to help you navigate your educational journey.
                        </Typography>

                        <Stack spacing={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton sx={{ bgcolor: '#25D366', color: 'white', mr: 2, '&:hover': { opacity: 0.9 } }}>
                                    <WhatsAppIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">WhatsApp</Typography>
                                    <Typography variant="h6">+92 300 7370282</Typography>
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="h6" gutterBottom>Follow Us</Typography>
                                <Stack direction="row" spacing={1}>
                                    <IconButton color="primary"><LinkedInIcon /></IconButton>
                                    <IconButton color="primary"><InstagramIcon /></IconButton>
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Form Side */}
                    <Grid item xs={12} md={7}>
                        <Paper elevation={0} sx={{
                            p: 4,
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: 'background.paper'
                        }}>
                            <Box component="form" onSubmit={handleSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Your Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Message"
                                            name="message"
                                            multiline
                                            rows={4}
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            startIcon={<WhatsAppIcon />}
                                            sx={{
                                                py: 2,
                                                fontWeight: 'bold',
                                                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                                                bgcolor: '#25D366',
                                                '&:hover': {
                                                    bgcolor: '#128C7E'
                                                }
                                            }}
                                        >
                                            Contact via WhatsApp
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Contact;
